import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// FastAPI 422s return `detail` as an array of Pydantic error objects rather
// than a string — stringify those into a readable message instead of
// letting them fall through as "[object Object]".
export const extractErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc.at(-1) : null;
        return field ? `${field}: ${item.msg}` : item?.msg;
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join("; ");
  }
  return fallback;
};

// ReimbursementView (nested) → flat UI object used by reimbursement components
const mapReimbursementViewToUI = ({ id, created_at, reimbursement }) => ({
  id,
  created_at,
  name: reimbursement.name ?? "",
  amount: reimbursement.amount != null ? String(reimbursement.amount) : "",
  paid_by: reimbursement.paid_by ?? null,
  payer: reimbursement.paid_by_name ?? "",
  payment_type: reimbursement.payment_type ?? "",
  date: reimbursement.date ?? "",
  status: reimbursement.status ?? "",
  approved_by: reimbursement.approved_by ?? null,
  approver_name: reimbursement.approver_name ?? "",
  // receipt_files (List[str] of paths) → receipt objects the UI expects
  receipts: (reimbursement.receipt_files ?? []).map((path) => ({
    path,
    file_name: path.split("/").pop() || path,
    stored_name: path,
  })),
});

// Flat UI object → ReimbursementEntry payload expected by the backend
const mapUIToPayload = (flat) => ({
  name: flat.name || null,
  amount: flat.amount !== "" && flat.amount != null ? flat.amount : null,
  paid_by: flat.paid_by ?? null,
  payment_type: flat.payment_type || null,
  date: flat.date || null,
  status: flat.status || null,
  approved_by: flat.approved_by ?? null,
  receipt_files: (flat.receipts ?? []).map((r) => r.path).filter(Boolean),
});

class ReimbursementService {
  /** List reimbursements with optional search, status filter, and pagination. */
  getReimbursements = async (
    search = "",
    limit = 50,
    skip = 0,
    filters = {},
  ) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    if (filters.status?.length) {
      filters.status.forEach((s) => params.append("status", s));
    }
    if (filters.paid_by != null) {
      params.append("paid_by", String(filters.paid_by));
    }
    const { data } = await apiClient.get(`/api/reimbursements?${params}`);
    return {
      ...data,
      items: data.items.map(mapReimbursementViewToUI),
    };
  };

  /** Fetch a single reimbursement by id. Returns flat UI object. */
  getReimbursement = async (id) => {
    const { data } = await apiClient.get(`/api/reimbursements/${id}`);
    return mapReimbursementViewToUI(data);
  };

  /** Create a reimbursement. payload = flat UI fields object. */
  createReimbursement = async (payload) => {
    try {
      const { data } = await apiClient.post(
        "/api/reimbursements",
        mapUIToPayload(payload),
      );
      return mapReimbursementViewToUI(data);
    } catch (err) {
      throw new Error(extractErrorMessage(err, "Failed to create reimbursement"));
    }
  };

  /** Update a reimbursement. payload = flat UI fields object. */
  updateReimbursement = async (id, payload) => {
    try {
      const { data } = await apiClient.put(
        `/api/reimbursements/${id}`,
        mapUIToPayload(payload),
      );
      return mapReimbursementViewToUI(data);
    } catch (err) {
      throw new Error(extractErrorMessage(err, "Failed to update reimbursement"));
    }
  };

  /** Upload a receipt file. Returns { file_name, stored_name, path }. */
  uploadReceiptFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post(
      "/api/reimbursements/receipts/upload",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return data;
  };

  /** Build a direct URL for a stored receipt path (for <a href> / Open link). */
  getReceiptFileUrl = (path) => {
    const params = new URLSearchParams({ path });
    return `${apiBaseUrl}/api/reimbursements/receipts/file?${params}`;
  };

  /** Fetch a receipt as a Blob for in-page preview. Returns { blob, contentType }. */
  getReceiptFile = async (path) => {
    const res = await apiClient.get("/api/reimbursements/receipts/file", {
      params: { path },
      responseType: "blob",
      validateStatus: (status) => status >= 200 && status < 300,
    });
    return {
      blob: res.data,
      contentType: res.headers["content-type"] || "application/octet-stream",
    };
  };

  /** Delete a reimbursement by id. */
  deleteReimbursement = async (id) => {
    const res = await apiClient.delete(`/api/reimbursements/${id}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /** Export reimbursements to an Excel blob. */
  exportReimbursements = async ({ search, status, paid_by } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status?.length) status.forEach((s) => params.append("status", s));
    if (paid_by != null) params.set("paid_by", String(paid_by));

    const res = await apiClient.get(`/api/reimbursements/export?${params}`, {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      validateStatus: (s) => s >= 200 && s < 300,
    });
    return res.data; // Blob
  };
}

export const reimbursementService = new ReimbursementService();
